import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { DateRange } from "@/hooks/useDateRange";
import { analyticsOperationsService } from "@/services/analytics-operations-service";
import {
  getPreviousAnalyticsDateRange,
  toAnalyticsDateParam,
} from "../utils/analyticsDateUtils";
import { useAnalyticsOverviewRange } from "./useAnalyticsOverviewRange";

export const analyticsOperationsKeys = {
  all: ["analytics-operations"] as const,
  totalRequests: (hotelId: string, from: string, to: string) =>
    [
      ...analyticsOperationsKeys.all,
      "total-requests",
      hotelId,
      from,
      to,
    ] as const,
  processingTime: (hotelId: string, from: string, to: string) =>
    [
      ...analyticsOperationsKeys.all,
      "processing-time",
      hotelId,
      from,
      to,
    ] as const,
  approvalTime: (hotelId: string, from: string, to: string) =>
    [
      ...analyticsOperationsKeys.all,
      "approval-time",
      hotelId,
      from,
      to,
    ] as const,
  onTimeRate: (hotelId: string, from: string, to: string) =>
    [
      ...analyticsOperationsKeys.all,
      "on-time-rate",
      hotelId,
      from,
      to,
    ] as const,
  complaints: (hotelId: string, from: string, to: string) =>
    [...analyticsOperationsKeys.all, "complaints", hotelId, from, to] as const,
  underestimatedServices: (hotelId: string, from: string, to: string) =>
    [
      ...analyticsOperationsKeys.all,
      "underestimated-services",
      hotelId,
      from,
      to,
    ] as const,
  onHoldCanceled: (hotelId: string, from: string, to: string) =>
    [
      ...analyticsOperationsKeys.all,
      "on-hold-canceled",
      hotelId,
      from,
      to,
    ] as const,
};

export function useOperationsAnalytics(dateRange: DateRange) {
  const rangeParams = useAnalyticsOverviewRange(dateRange);
  const { hotelId, from, to, isReady } = rangeParams;
  const previousRangeQuery = useMemo(() => {
    const previousRange = getPreviousAnalyticsDateRange(dateRange);
    const previousFrom = toAnalyticsDateParam(previousRange?.from);
    const previousTo = toAnalyticsDateParam(previousRange?.to);

    if (!previousFrom || !previousTo) {
      return null;
    }

    return {
      hotelId,
      from: previousFrom,
      to: previousTo,
    };
  }, [dateRange, hotelId]);

  const totalRequests = useQuery({
    queryKey: analyticsOperationsKeys.totalRequests(hotelId, from, to),
    queryFn: () =>
      analyticsOperationsService.getTotalRequests({ hotelId, from, to }),
    enabled: isReady,
  });

  const processingTime = useQuery({
    queryKey: analyticsOperationsKeys.processingTime(hotelId, from, to),
    queryFn: () =>
      analyticsOperationsService.getProcessingTime({ hotelId, from, to }),
    enabled: isReady,
  });

  const approvalTime = useQuery({
    queryKey: analyticsOperationsKeys.approvalTime(hotelId, from, to),
    queryFn: () =>
      analyticsOperationsService.getApprovalTime({ hotelId, from, to }),
    enabled: isReady,
  });

  const onTimeRate = useQuery({
    queryKey: analyticsOperationsKeys.onTimeRate(hotelId, from, to),
    queryFn: () =>
      analyticsOperationsService.getOnTimeRate({ hotelId, from, to }),
    enabled: isReady,
  });

  const complaints = useQuery({
    queryKey: analyticsOperationsKeys.complaints(hotelId, from, to),
    queryFn: () =>
      analyticsOperationsService.getComplaints({ hotelId, from, to }),
    enabled: isReady,
  });

  const underestimatedServices = useQuery({
    queryKey: analyticsOperationsKeys.underestimatedServices(hotelId, from, to),
    queryFn: () =>
      analyticsOperationsService.getUnderestimatedServices({
        hotelId,
        from,
        to,
      }),
    enabled: isReady,
  });

  const onHoldCanceled = useQuery({
    queryKey: analyticsOperationsKeys.onHoldCanceled(hotelId, from, to),
    queryFn: () =>
      analyticsOperationsService.getOnHoldCanceled({ hotelId, from, to }),
    enabled: isReady,
  });

  const totalRequestsPrevious = useQuery({
    queryKey: previousRangeQuery
      ? analyticsOperationsKeys.totalRequests(
          previousRangeQuery.hotelId,
          previousRangeQuery.from,
          previousRangeQuery.to,
        )
      : [...analyticsOperationsKeys.all, "total-requests-previous-disabled"],
    queryFn: () =>
      analyticsOperationsService.getTotalRequests(previousRangeQuery!),
    enabled: isReady && previousRangeQuery != null,
  });

  const processingTimePrevious = useQuery({
    queryKey: previousRangeQuery
      ? analyticsOperationsKeys.processingTime(
          previousRangeQuery.hotelId,
          previousRangeQuery.from,
          previousRangeQuery.to,
        )
      : [...analyticsOperationsKeys.all, "processing-time-previous-disabled"],
    queryFn: () =>
      analyticsOperationsService.getProcessingTime(previousRangeQuery!),
    enabled: isReady && previousRangeQuery != null,
  });

  const onTimeRatePrevious = useQuery({
    queryKey: previousRangeQuery
      ? analyticsOperationsKeys.onTimeRate(
          previousRangeQuery.hotelId,
          previousRangeQuery.from,
          previousRangeQuery.to,
        )
      : [...analyticsOperationsKeys.all, "on-time-rate-previous-disabled"],
    queryFn: () =>
      analyticsOperationsService.getOnTimeRate(previousRangeQuery!),
    enabled: isReady && previousRangeQuery != null,
  });

  const complaintsPrevious = useQuery({
    queryKey: previousRangeQuery
      ? analyticsOperationsKeys.complaints(
          previousRangeQuery.hotelId,
          previousRangeQuery.from,
          previousRangeQuery.to,
        )
      : [...analyticsOperationsKeys.all, "complaints-previous-disabled"],
    queryFn: () =>
      analyticsOperationsService.getComplaints(previousRangeQuery!),
    enabled: isReady && previousRangeQuery != null,
  });

  const queries = [
    totalRequests,
    processingTime,
    approvalTime,
    onTimeRate,
    complaints,
    underestimatedServices,
    onHoldCanceled,
    totalRequestsPrevious,
    processingTimePrevious,
    onTimeRatePrevious,
    complaintsPrevious,
  ];

  return {
    ...rangeParams,
    totalRequests,
    processingTime,
    approvalTime,
    onTimeRate,
    complaints,
    underestimatedServices,
    onHoldCanceled,
    totalRequestsPrevious,
    processingTimePrevious,
    onTimeRatePrevious,
    complaintsPrevious,
    isLoading: queries.some((item) => item.isLoading),
    isFetching: queries.some((item) => item.isFetching),
    isError: queries.some((item) => item.isError),
    error: queries.find((item) => item.error)?.error,
  };
}
